import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { http } from "@/lib/api";
import { LetterBadge, GRADING_SCALE } from "@/components/records/RecordShell";

const exportCsv = (d, name) => {
  const header = ["Student", ...d.subjects.map((s) => s.short), "Overall", "Letter"];
  const rows = d.rows.map((r) => [r.name, ...d.subjects.map((s) => r.subjects[s.id]?.average ?? ""), r.average ?? "", r.letter ?? ""]);
  const csv = [header, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name.replace(/\W+/g, "_")}_gradebook.csv`;
  a.click();
};

export const Gradebook = ({ classId, className }) => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/classes/${classId}/gradebook`).then((r) => setD(r.data)); }, [classId]);
  if (!d) return <p className="text-stone-500">Loading gradebook…</p>;

  return (
    <div data-testid="gradebook-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-stone-500">School year {d.school_year} to date · {GRADING_SCALE}</p>
        <button onClick={() => exportCsv(d, className)} data-testid="gradebook-export-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6DEC3] text-sm hover:bg-[#F1EADB] transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto rounded-3xl border border-[#E6DEC3] bg-white/60">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E6DEC3] text-xs text-stone-500 text-left">
              <th className="px-4 py-3 font-semibold uppercase tracking-widest">Student</th>
              {d.subjects.map((s) => <th key={s.id} className="px-3 py-3 font-medium whitespace-nowrap">{s.short}</th>)}
              <th className="px-3 py-3 font-semibold">Overall</th>
              <th className="px-3 py-3 font-semibold">Records</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.student_id} className="border-b border-[#E6DEC3]/60 last:border-0" data-testid={`gradebook-row-${r.student_id}`}>
                <td className="px-4 py-3 font-medium text-[#2C4A3E] whitespace-nowrap">{r.name}</td>
                {d.subjects.map((s) => {
                  const g = r.subjects[s.id];
                  return <td key={s.id} className="px-3 py-3">{g ? <span className="inline-flex items-center gap-1.5">{g.average}<span className="text-xs text-stone-500">{g.letter}</span></span> : <span className="text-stone-300">—</span>}</td>;
                })}
                <td className="px-3 py-3"><span className="inline-flex items-center gap-2"><b data-testid={`gradebook-overall-${r.student_id}`}>{r.average ?? "—"}</b><LetterBadge letter={r.letter} /></span></td>
                <td className="px-3 py-3 whitespace-nowrap text-xs">
                  <Link to={`/report/${r.student_id}`} className="text-[#C87D53] mr-3" data-testid={`gradebook-report-${r.student_id}`}>Report card</Link>
                  <Link to={`/transcript/${r.student_id}`} className="text-[#C87D53]" data-testid={`gradebook-transcript-${r.student_id}`}>Transcript</Link>
                </td>
              </tr>
            ))}
            {!d.rows.length && <tr><td colSpan={d.subjects.length + 3} className="px-5 py-10 text-center text-stone-500">No students yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
